import React from 'react';
import { StakingActivationStatus } from '../types';

interface StakingReactivationCardProps {
  status: StakingActivationStatus | null;
  loading: boolean;
  processing: boolean;
  onReactivate: () => void;
}

const formatDate = (value: string) => new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
  timeZone: 'Europe/Warsaw',
}).format(new Date(value));

const StakingReactivationCard: React.FC<StakingReactivationCardProps> = ({
  status,
  loading,
  processing,
  onReactivate,
}) => {
  if (loading && !status) {
    return (
      <div className="rounded-2xl border border-white/10 bg-black/80 p-5 text-center text-white/70">
        Loading staking activation status...
      </div>
    );
  }

  if (!status) return null;

  const inactive = !status.staking_active;
  const actionAvailable = status.can_reactivate;
  const theme = inactive
    ? 'from-red-950/95 via-rose-950/90 to-black border-red-400/40 shadow-red-500/10'
    : status.renewal_window_open
    ? 'from-amber-950/95 via-orange-950/90 to-black border-amber-400/40 shadow-amber-500/10'
    : 'from-emerald-950/95 via-cyan-950/80 to-black border-emerald-400/30 shadow-emerald-500/10';

  const title = inactive
    ? 'Staking inactive — reactivate for free'
    : status.renewal_window_open
    ? 'Staking renewal is now available'
    : 'Staking is active';

  return (
    <section className={`relative overflow-hidden rounded-2xl border bg-gradient-to-r p-5 shadow-xl ${theme}`}>
      <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-4">
          <div className={`mt-1 h-3 w-3 flex-shrink-0 rounded-full ${
            inactive ? 'bg-red-400 animate-pulse' : status.renewal_window_open ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400'
          }`} />
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Free staking activation</p>
            <h2 className="mt-1 text-xl font-extrabold text-white">{title}</h2>
            {inactive ? (
              <p className="mt-2 text-sm text-red-100/80">
                Rewards have been paused for <strong>{status.staking_inactive_days} day{status.staking_inactive_days === 1 ? '' : 's'}</strong>.
                Reactivation resumes earning from today, without withdrawing funds or making an on-chain transaction.
              </p>
            ) : status.renewal_window_open ? (
              <p className="mt-2 text-sm text-amber-100/80">
                Confirm by <strong>{formatDate(status.staking_valid_until)}</strong> to keep earning without interruption.
                {` ${status.days_until_deadline} day${status.days_until_deadline === 1 ? '' : 's'} remaining.`}
              </p>
            ) : (
              <p className="mt-2 text-sm text-emerald-100/80">
                Active until <strong>{formatDate(status.staking_valid_until)}</strong>. Renewal opens on{' '}
                <strong>{formatDate(status.renewal_window_opens_at)}</strong>.
              </p>
            )}
            {status.staking_renewed_at && (
              <p className="mt-2 text-xs text-white/45">
                Last confirmed: {formatDate(status.staking_renewed_at)}
              </p>
            )}
            {status.staking_active && status.staking_inactive_days > 0 && (
              <p className="mt-1 text-xs text-white/45">
                Previous inactivity period: {status.staking_inactive_days} day{status.staking_inactive_days === 1 ? '' : 's'}
              </p>
            )}
          </div>
        </div>

        {actionAvailable && (
          <button
            type="button"
            onClick={onReactivate}
            disabled={processing}
            className={`min-w-[220px] rounded-xl px-5 py-3 font-extrabold text-white shadow-lg transition-all duration-200
                       hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100 ${
              inactive
                ? 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-400 hover:to-rose-500'
                : 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500'
            }`}
          >
            {processing ? 'Confirming...' : inactive ? 'Reactivate staking for free' : 'Extend staking for free'}
          </button>
        )}
      </div>
    </section>
  );
};

export default StakingReactivationCard;
